import { useContext, useEffect, useState } from 'react';
import { useDebounceCallback } from '@react-hook/debounce';
import { ForecastFiltersContext } from '../../../state/filters/contexts';
import usePmSensorAuthId from 'components/hooks/PM/usePmSensorAuthId';
import { getForecasting } from 'common/services/project-progression.service';
import { resourceAllocationAggregationRangesEnum } from '@linearb/linjs-common';
import useForecastParsedData from './useForecastParsedData';
import { PERIODS } from 'components/PeriodPicker/periods/constants';
import { size } from 'lodash';
import { FORECAST_EMPTY_STATES_TYPES } from '../EmptyState/useEmptyStateType';

const useForecast = () => {
  const {
    state: { filtersPayload, extraPayload, isInitialized, selectedFilterSetId }
  } = useContext(ForecastFiltersContext);
  const pmSensorAuthId = usePmSensorAuthId();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorType, setErrorType] = useState(null);
  const defaultPeriod = PERIODS.PAST_6_MONTHS;

  const { totalSimulations, issuesStates, finishedWeeks, unfinished } =
    useForecastParsedData(data);

  const isEmptyFilters = size(filtersPayload) === 0;

  const fetchForecast = useDebounceCallback(async () => {
    try {
      if (isEmptyFilters) throw new Error();

      const res = await getForecasting({
        sensor_authorization_id: pmSensorAuthId,
        after: defaultPeriod.after,
        aggregation_period: resourceAllocationAggregationRangesEnum.WEEK,
        type: 'issues',
        filter_set_id: selectedFilterSetId,
        filtersPayload,
        ...extraPayload
      });
      setData(res);
    } catch (e) {
      setData(null);
      const errorType =
        e?.response?.data?.description || FORECAST_EMPTY_STATES_TYPES.DEFAULT;
      setErrorType(errorType);
    } finally {
      setIsLoading(false);
    }
  }, 500);

  useEffect(() => {
    if (!isInitialized) return;
    setIsLoading(true);
    setErrorType(null);
    fetchForecast();
  }, [isInitialized, selectedFilterSetId, filtersPayload]);

  return {
    isLoading,
    totalSimulations,
    issuesStates,
    finishedWeeks,
    unfinished,
    errorType
  };
};

export default useForecast;
